import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Users } from 'lucide-react';

interface AccountType {
  id: 'founder' | 'group_a';
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  features: string[];
}

const AccountTypeSelection: React.FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<'founder' | 'group_a' | ''>('');

  const handleContinue = () => {
    if (selectedType) {
      navigate('/register', { state: { accountType: selectedType } });
    }
  };

  const accountTypes: AccountType[] = [
    {
      id: 'founder',
      title: 'Founder',
      description: 'Create and manage startup pitches, roadmaps, and connect with investors',
      icon: User,
      features: [
        'Create startup pitches',
        'Build AI-assisted roadmaps',
        'Manage your startup portfolio',
        'Connect with investors',
        'Track funding progress'
      ]
    },
    {
      id: 'group_a',
      title: 'Investor',
      description: 'Browse startup pitches, discover innovative ideas, and support founders',
      icon: Users,
      features: [
        'Browse startup pitches',
        'Discover innovative ideas',
        'Support founders',
        'View funded startups',
        'Stay updated on trends'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <div className="flex items-center p-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="text-center mb-8">
          <p className="text-gray-400 text-lg max-w-full mb-32">
            Select the account type that best describes your role in the startup ecosystem
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-white mb-4">
            Choose Your Account Type
          </h1>
          
        </div>

        {/* Account Type Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl w-full mb-8">
          {accountTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType === type.id;
            
            return (
              <div
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`card cursor-pointer transition-all duration-300 hover:scale-105 ${
                  isSelected 
                    ? 'ring-2 ring-primary-500 bg-gray-700' 
                    : 'hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-full mr-4 ${
                    isSelected ? 'bg-primary-500' : 'bg-gray-600'
                  }`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{type.title}</h3>
                    {/* <p className="text-gray-400 text-sm">{type.description}</p> */}
                  </div>
                </div>

                <ul className="space-y-2">
                  {type.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300 text-sm">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!selectedType}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
            selectedType
              ? 'bg-primary-600 hover:bg-primary-700 text-white'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default AccountTypeSelection; 